import { cva, type VariantProps } from 'class-variance-authority';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

const alertVariants = cva(
  'flex gap-3 rounded-lg border px-4 py-3 text-sm [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        info: 'border-border bg-surface-muted text-ink',
        success: 'border-positive/30 bg-positive-subtle text-ink',
        warning: 'border-warning/30 bg-warning-subtle text-ink',
        error: 'border-critical/30 bg-critical-subtle text-ink',
      },
    },
    defaultVariants: { tone: 'info' },
  },
);

const ICONS: Record<string, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

const ICON_TONE: Record<string, string> = {
  info: 'text-ink-subtle',
  success: 'text-positive',
  warning: 'text-warning',
  error: 'text-critical',
};

export type AlertProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & { title?: string };

export const Alert = ({
  className,
  tone = 'info',
  title,
  children,
  ...props
}: AlertProps) => {
  const Icon = ICONS[tone ?? 'info'];

  return (
    <div
      // Errors interrupt; everything else waits for a pause in speech.
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(alertVariants({ tone }), className)}
      {...props}
    >
      <Icon className={ICON_TONE[tone ?? 'info']} aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? (
          <div className={cn('text-ink-muted', title && 'mt-0.5')}>
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
};
